


























































import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { vxm } from '@/store';
import OtTag, { TagStatus } from '@/components/global/ot-tag.vue';
import OtButton from '@/components/global/ot-button.vue';
import OtTextField from '@/components/global/ot-text-field.vue';
import OtTextarea from '@/components/global/ot-textarea.vue';
import OtPhoneNumberField from '@/components/global/ot-phone-number-field.vue';
import OtApi, { executeApi } from '@/services/api.service';
import OtStandardHeaderArchetype from '@/components/global/archetypes/ot-standard-header-archetype.vue';
import OtAddressField from '@/components/global/ot-address-field.vue';
import OtSelect from '@/components/global/ot-select.vue';
import OtTabs from '@/components/global/ot-tabs.vue';
import OtComboBox from '@/components/global/ot-combo-box.vue';
import { ROUTE_SETTINGS_ORG_USER, ROUTE_SETTINGS_ORG_USER_EDIT } from '../settings-routes';
import { IInputTab } from '@/components/global/common-models';
import { dirtyFormClass } from '@/utils/validation-utils';
import { IAutocompleteItem } from '@/components/global/ot-autocomplete.vue';
import { blurCombos, IVForm } from '@/utils/type-utils';
import { OtUserStatus, OtStatusType } from '@/types/status-enums';
import { OrganisationUserDetails, OrganisationUserDetailsFormObject } from '../models/organisation-user-models';

@Component({
  components: {
    OtStandardHeaderArchetype,
    OtTabs,
    OtTag,
    OtButton,
    OtTextField,
    OtTextarea,
    OtPhoneNumberField,
    OtAddressField,
    OtSelect,
    OtComboBox,
  },
})
export default class OtEditOrganisationUserDetails extends Vue {
  // * PROPS

  // * REFS
  @Ref('orgUserDetailsFormRef') private readonly orgUserDetailsFormRef!: IVForm;

  // * DATA
  private api = new OtApi();
  private formData = OrganisationUserDetailsFormObject.createEmpty();
  private organisationUser = OrganisationUserDetails.createEmpty();
  private organisationUserJobTitles: string[] = [];
  private isLoading = true;
  private originalThumbprint = '';
  private currentThumbprint = '';
  private saving = false;

  private get settingsTabs(): IInputTab[] {
    return [
      {
        tabId: 0,
        tabText: 'USER PROFILE',
        tabRoute: {
          params: { orgId: this.orgId },
          name: ROUTE_SETTINGS_ORG_USER_EDIT,
        },
      },
    ];
  }

  // Validation rules

  public validate() {
    return this.orgUserDetailsFormRef.validate();
  }

  // * COMPUTED

  private get userStatusGid(): string {
    return this.organisationUser.organisationUserStatus?.gid || '';
  }

  private get orgStatus(): OtUserStatus {
    const status = this.organisationUser.organisationUserStatus?.status;
    // NOTE - the default here is inactive - it's never going to hit the other side of the || ?
    return status || OtUserStatus.Inactive;
  }

  private get dirtyFormClass(): string {
    return dirtyFormClass;
  }

  private get formIsDirty(): boolean {
    return this.originalThumbprint !== this.currentThumbprint;
  }

  get organisationUserJobTitleItems(): IAutocompleteItem<string>[] {
    return this.organisationUserJobTitles.map(t => {
      return {
        label: t,
        data: t,
      };
    });
  }

  get orgUsersRoute(): string {
    return ROUTE_SETTINGS_ORG_USER;
  }

  private get orgId() {
    return this.$route.params.orgId || vxm.userProfile.defaultOrganisationId || '';
  }

  private get userGid() {
    return this.$route.params.userGid || '';
  }
  private get userName() {
    return this.organisationUser.firstName + ' ' + this.organisationUser.lastName || '';
  }
  private get orgName() {
    return this.organisationUser.organisationName || '';
  }

  // * WATCHERS

  @Watch('routePath')
  private async checkRoutePath() {
    if (!this.organisationUser || this.organisationUser.gid !== this.userGid) {
      this.isLoading = true;
      await this.getOrganisationUserDetails();
      this.setOrgNameToBreadcrumb();
      this.isLoading = false;
    }
  }

  // watch the organisation user details and update the thumbprint
  @Watch('formData', { deep: true })
  private formDataChanged(val: OrganisationUserDetailsFormObject) {
    this.currentThumbprint = this.getOrganisationUserDetailsThumbprint(val);
  }

  // * METHODS

  private navigateBackward() {
    // according to the ENG we literally just go back in the browser history
    return this.$router.back();
  }

  private getTagStatus(status: OtUserStatus): TagStatus {
    return { type: OtStatusType.User, status };
  }

  private setOrgNameToBreadcrumb() {
    vxm.breadcrumbs.setOrganisationName({ organisation: this.orgName });
  }
  private setUserNameToBreadcrumb() {
    vxm.breadcrumbs.setUserName({ userName: this.userName });
  }

  public setFormToCleanState() {
    this.originalThumbprint = this.currentThumbprint;
  }

  private getOrganisationUserDetailsThumbprint(orgUserDetails: OrganisationUserDetailsFormObject): string {
    const vals = {
      jobTitle: orgUserDetails.jobTitle || '',
    };
    return JSON.stringify(vals);
  }

  private async updateOrganisationUserDetails() {
    blurCombos(this.$refs.jobTitleCombo);
    this.$nextTick(() => {
      this.updateOrganisationUserDetailsInner();
    });
  }

  private async updateOrganisationUserDetailsInner() {
    const valid = this.validate();

    if (valid && this.formData) {
      this.saving = true;
      const requestModel = OrganisationUserDetailsFormObject.createRequestModel(this.formData);

      const apiResponse = await executeApi(
        () => this.api.organisationUser().postUpdateOrganisationUserDetails(this.userStatusGid, requestModel),
        'Update Organisation User Details',
      );
      if (apiResponse && apiResponse.success) {
        this.setFormToCleanState();
        // Even though we set the form to be clean just above,
        // the dirty modal still appears unless we reroute on the next tick
        this.$nextTick(() => {
          this.navigateBackward();
        });
      }
      this.saving = false;
    }
  }

  private async getOrganisationUserDetails() {
    const result = await executeApi(
      () => this.api.organisation().getOrganisationUserDetails(this.orgId, this.userGid),
      'Load Organisation User Details',
    );
    if (result.success && result.data && result.data) {
      this.organisationUser = OrganisationUserDetails.createFromApiResponse(result.data);
      this.formData = OrganisationUserDetailsFormObject.createFromApiResponse(result.data);
      this.originalThumbprint = this.getOrganisationUserDetailsThumbprint(this.formData);
      this.currentThumbprint = this.originalThumbprint;
      this.setUserNameToBreadcrumb();
    }
  }
  private async getOrganisationUserJobTitles() {
    const result = await executeApi(
      () => this.api.organisationUser().getOrganisationUserJobTitles(),
      'Load Job Titles',
    );
    if (result.success && result.data && result.data) {
      this.organisationUserJobTitles = result.data;
    }
  }

  // * LIFECYCLE
  private created() {
    this.checkRoutePath();
    this.getOrganisationUserJobTitles();
    if (this.orgName) {
      vxm.breadcrumbs.setOrganisationName({ organisation: this.orgName });
    }
  }
}
